import {
  Avatar,
  Button,
  DatePicker,
  Divider,
  Drawer,
  Input,
  List,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useAuth } from "../../lib/auth";
import type { SubtaskItem, TaskStatus } from "../../lib/types";
import {
  taskStatusOptions,
  type ProjectItem,
  type UserOption,
} from "./types";

interface SubtaskDrawerProps {
  subtask: SubtaskItem | null;
  project: ProjectItem;
  users: UserOption[];
  subtaskCommentDrafts: Record<string, string>;
  setSubtaskCommentDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onClose: () => void;
  onUpdateSubtask: (
    subtask: SubtaskItem,
    updates: Partial<SubtaskItem>,
  ) => Promise<void> | void;
  onUpdateSubtaskStatus: (
    subtask: SubtaskItem,
    status: TaskStatus,
  ) => Promise<void> | void;
  onAddSubtaskComment: (subtaskId: string) => Promise<void> | void;
}

export default function SubtaskDrawer({
  subtask,
  project: _project,
  users,
  subtaskCommentDrafts,
  setSubtaskCommentDrafts,
  onClose,
  onUpdateSubtask,
  onUpdateSubtaskStatus,
  onAddSubtaskComment,
}: SubtaskDrawerProps) {
  const { canWrite } = useAuth();
  const ro = !canWrite;
  return (
    <Drawer
      title="Subtask Details"
      placement="right"
      open={!!subtask}
      onClose={onClose}
      width={420}
    >
      {subtask ? (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Title level={5}>{subtask.title}</Typography.Title>
          <Typography.Text type="secondary">Completion status</Typography.Text>
          <Select
            aria-label="Subtask completion status"
            value={subtask.status}
            onChange={(value) => onUpdateSubtaskStatus(subtask, value)}
            options={taskStatusOptions}
            disabled={ro}
          />
          <Typography.Text type="secondary">Assigned team member</Typography.Text>
          <Select
            aria-label="Subtask assignee"
            value={subtask.assignedTo || undefined}
            onChange={(value) => onUpdateSubtask(subtask, { assignedTo: value || null })}
            allowClear
            options={users.map((userItem) => ({
              value: userItem.uid,
              label: userItem.displayName || userItem.email,
            }))}
            disabled={ro}
          />
          <Typography.Text type="secondary">Start date</Typography.Text>
          <DatePicker
            aria-label="Subtask start date"
            value={subtask.startDate ? dayjs(subtask.startDate) : null}
            onChange={(date) =>
              onUpdateSubtask(subtask, {
                startDate: date ? date.format("YYYY-MM-DD") : undefined,
              })
            }
            style={{ width: "100%" }}
            disabled={ro}
          />
          <Typography.Text type="secondary">Due date</Typography.Text>
          <DatePicker
            aria-label="Subtask due date"
            value={subtask.dueDate ? dayjs(subtask.dueDate) : null}
            onChange={(date) =>
              onUpdateSubtask(subtask, {
                dueDate: date ? date.format("YYYY-MM-DD") : null,
              })
            }
            style={{ width: "100%" }}
            disabled={ro}
          />
          <Typography.Text type="secondary">Dependencies</Typography.Text>
          <Input
            aria-label="Subtask dependencies"
            value={subtask.dependencies || ""}
            onChange={(event) =>
              onUpdateSubtask(subtask, { dependencies: event.target.value })
            }
            disabled={ro}
          />
          <Divider />
          <Typography.Text strong>Comments history</Typography.Text>
          {(subtask.comments || []).length === 0 ? (
            <Typography.Text type="secondary">No comments yet.</Typography.Text>
          ) : (
            <List
              aria-label="Subtask comments"
              dataSource={subtask.comments || []}
              renderItem={(comment) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar>{comment.authorName?.[0] || "U"}</Avatar>}
                    title={
                      <Space size="small">
                        <Typography.Text strong>
                          {comment.authorName || "User"}
                        </Typography.Text>
                        {comment.createdAt && (
                          <Typography.Text type="secondary">
                            {new Date(comment.createdAt).toLocaleString()}
                          </Typography.Text>
                        )}
                      </Space>
                    }
                    description={<Typography.Text>{comment.message}</Typography.Text>}
                  />
                </List.Item>
              )}
            />
          )}
          <Input.TextArea
            aria-label="Add subtask comment"
            value={subtaskCommentDrafts[subtask.id] || ""}
            onChange={(event) =>
              setSubtaskCommentDrafts((prev) => ({
                ...prev,
                [subtask.id]: event.target.value,
              }))
            }
            placeholder="Add a subtask comment..."
            autoSize={{ minRows: 2 }}
          />
          <Button
            type="primary"
            onClick={() => onAddSubtaskComment(subtask.id)}
            disabled={!(subtaskCommentDrafts[subtask.id] || "").trim()}
          >
            Add Comment
          </Button>
        </Space>
      ) : null}
    </Drawer>
  );
}
